import type { SearchResponse, SearchResultItem } from '../types/api'
import { apiClient } from './client'
import type { MemoryItem } from './memory'

export interface SearchFilters {
  docTypes?: string[]
  tags?: string[]
}

/**
 * Order results best match first. `score` is an ascending distance (0 = best),
 * so sorting ascending puts the strongest match at the top.
 */
function sortByBestMatch(results: SearchResultItem[]): SearchResultItem[] {
  return [...results].sort((a, b) => a.score - b.score)
}

/** Memories use higher-is-better scores when present. */
function sortMemoriesByScore(memories: MemoryItem[]): MemoryItem[] {
  return [...memories].sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
}

export async function searchProject(
  projectName: string,
  query: string,
  limit?: number,
  filters?: SearchFilters
): Promise<SearchResponse> {
  const response = await apiClient.post<SearchResponse>('/search', {
    projectName,
    query,
    limit,
    ...(filters?.docTypes && filters.docTypes.length > 0 ? { docTypes: filters.docTypes } : {}),
    ...(filters?.tags && filters.tags.length > 0 ? { tags: filters.tags } : {}),
  })
  return { ...response, results: sortByBestMatch(response.results ?? []) }
}

export interface ContextSearchResponse {
  knowledge: SearchResultItem[]
  memories: MemoryItem[]
}

/**
 * Unified retrieval: document chunks + project memories via POST /context/search.
 */
export async function searchContext(
  projectName: string,
  query: string,
  limit?: number,
  filters?: SearchFilters
): Promise<ContextSearchResponse> {
  const response = await apiClient.post<{
    knowledge?: SearchResultItem[]
    memories?: MemoryItem[]
  }>('/context/search', {
    projectName,
    query,
    knowledgeLimit: limit,
    memoryLimit: limit,
    ...(filters?.docTypes && filters.docTypes.length > 0 ? { docTypes: filters.docTypes } : {}),
    ...(filters?.tags && filters.tags.length > 0 ? { tags: filters.tags } : {}),
  })
  return {
    knowledge: sortByBestMatch(response.knowledge ?? []),
    memories: sortMemoriesByScore(response.memories ?? []),
  }
}

export async function parseFilters(
  projectName: string,
  query: string
): Promise<{ docTypes: string[]; tags: string[] }> {
  return apiClient.post<{ docTypes: string[]; tags: string[] }>('/search/parse-filters', {
    projectName,
    query,
  })
}

export interface AnswerChunk {
  type: 'sources' | 'content' | 'done' | 'error'
  results?: SearchResultItem[]
  memories?: MemoryItem[]
  content?: string
  message?: string
}

/**
 * Stream a RAG-powered answer from the /search/answer endpoint.
 * Calls onChunk for each SSE event received from the server.
 */
export async function streamAnswer(
  projectName: string,
  query: string,
  limit: number,
  onChunk: (chunk: AnswerChunk) => void,
  signal?: AbortSignal,
  filters?: SearchFilters
): Promise<void> {
  // Route through apiClient so the request carries the org-scoped base URL
  // (`/api/<orgSlug>`), CSRF token, and token-refresh handling. A hand-rolled
  // `fetch('/api/search/answer')` drops the org slug and 404s on multi-tenant
  // deployments (the tenant resolver reads the first path segment as an org).
  const response = await apiClient.postStream(
    '/search/answer',
    {
      projectName,
      query,
      limit,
      ...(filters?.docTypes && filters.docTypes.length > 0 ? { docTypes: filters.docTypes } : {}),
      ...(filters?.tags && filters.tags.length > 0 ? { tags: filters.tags } : {}),
    },
    signal
  )

  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed?.startsWith('data: ')) continue
        const data = trimmed.slice(6)
        try {
          const parsed = JSON.parse(data) as AnswerChunk
          if (parsed.results) parsed.results = sortByBestMatch(parsed.results)
          if (parsed.memories) parsed.memories = sortMemoriesByScore(parsed.memories)
          onChunk(parsed)
          if (parsed.type === 'done' || parsed.type === 'error') return
        } catch {
          // Skip malformed JSON
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}
